import React, { useMemo } from "react";
import {
  FileText, Send, RotateCcw, Trash2, Check, CalendarDays, X, Plus, User, Mail,
  Phone, Car, Receipt, Euro, Paperclip, Clock, CheckCircle, Ban, Search,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatCard, SectionCard, EmptyState, StatusBadge, FilterPill, SearchInput, ActionButton } from "./AdminUI";

const STATUS_CFG = {
  draft: { label: "Brouillon", color: "bg-white/[0.06] text-white/60 border-white/[0.1]", dot: "bg-white/40" },
  sent: { label: "Envoyé", color: "bg-blue-500/15 text-blue-400 border-blue-500/25", dot: "bg-blue-400" },
  accepted: { label: "Accepté", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", dot: "bg-emerald-400" },
  completed: { label: "Terminé", color: "bg-purple-500/15 text-purple-400 border-purple-500/25", dot: "bg-purple-400" },
  rejected: { label: "Refusé", color: "bg-red-500/15 text-red-400 border-red-500/25", dot: "bg-red-400" },
};

const PAY_CFG = {
  pending: { label: "En attente", color: "bg-white/[0.06] text-white/60 border-white/[0.1]", dot: "bg-white/40" },
  deposit_paid: { label: "Acompte payé", color: "bg-amber-500/15 text-amber-400 border-amber-500/25", dot: "bg-amber-400" },
  fully_paid: { label: "Payé 100%", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", dot: "bg-emerald-400" },
};

const QuotesTab = ({ ctx }) => {
  const {
    quotes, quoteForm, setQuoteForm, quoteLoading, quotePdfFile, setQuotePdfFile,
    handleCreateQuote, handleSendQuote, handleDeleteQuote,
    showCreateAppointmentFromQuote, setShowCreateAppointmentFromQuote,
    appointmentFromQuoteForm, setAppointmentFromQuoteForm,
    handleCreateAppointmentFromQuote, clients, timeSlots,
  } = ctx;

  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const counts = useMemo(() => ({
    total: quotes.length,
    sent: quotes.filter((q) => q.status === "sent").length,
    paid: quotes.filter((q) => q.payment_status === "fully_paid" || q.payment_status === "deposit_paid").length,
    pending: quotes.filter((q) => q.payment_status === "pending").length,
  }), [quotes]);

  const list = useMemo(() => {
    let arr = quotes;
    if (statusFilter !== "all") arr = arr.filter((q) => q.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((x) =>
        [x.customer_name, x.customer_email, x.description, x.vehicle_info, x.external_quote_number]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      );
    }
    return arr;
  }, [quotes, statusFilter, search]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Receipt size={20} className="text-[#FF3B30]" />
          Paiement & Devis
        </h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={FileText} label="Total devis" value={counts.total} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={Send} label="Envoyés" value={counts.sent} color="text-blue-400" accent="bg-blue-500/10" />
        <StatCard icon={CheckCircle} label="Payés" value={counts.paid} color="text-emerald-400" accent="bg-emerald-500/10" />
        <StatCard icon={Clock} label="En attente paiement" value={counts.pending} color="text-amber-400" accent="bg-amber-500/10" />
      </div>

      {/* Create quote form */}
      <SectionCard title="Créer un lien de paiement" icon={Plus} iconColor="text-emerald-400">
        <p className="text-white/50 text-sm mb-4">
          Créez votre devis dans Pennylane, puis envoyez ici le lien de paiement au client.
        </p>
        <form onSubmit={handleCreateQuote} className="space-y-4">
          {/* Existing client selector */}
          <div>
            <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Sélectionner un client existant (optionnel)</Label>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  const client = JSON.parse(e.target.value);
                  setQuoteForm({
                    ...quoteForm,
                    customer_name: client.client_name || client.name || "",
                    customer_email: client.client_email || client.email || "",
                    customer_phone: client.client_phone || client.phone || "",
                    vehicle_info: client.plates?.[0] || "",
                  });
                }
              }}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white rounded-md px-3 py-2 text-sm"
            >
              <option value="">— Nouveau client —</option>
              {clients.map((c, idx) => (
                <option key={c.client_email || idx} value={JSON.stringify(c)}>
                  {c.client_name || c.name} — {c.client_email || c.email} {c.plates?.[0] && `(${c.plates[0]})`}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><User size={11} /> Nom client *</Label>
              <Input required value={quoteForm.customer_name} onChange={(e) => setQuoteForm({ ...quoteForm, customer_name: e.target.value })} placeholder="Jean Dupont" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><Mail size={11} /> Email *</Label>
              <Input required type="email" value={quoteForm.customer_email} onChange={(e) => setQuoteForm({ ...quoteForm, customer_email: e.target.value })} placeholder="client@email.com" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><Phone size={11} /> Téléphone</Label>
              <Input value={quoteForm.customer_phone} onChange={(e) => setQuoteForm({ ...quoteForm, customer_phone: e.target.value })} placeholder="06 12 34 56 78" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><Car size={11} /> Plaque</Label>
              <Input value={quoteForm.vehicle_info} onChange={(e) => setQuoteForm({ ...quoteForm, vehicle_info: e.target.value })} placeholder="AB-123-CD" className="bg-[#1C1C1E] border-white/[0.06] text-[#FF3B30] font-bold h-9 text-sm" />
            </div>
          </div>

          {/* Pennylane block */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-3 flex items-center gap-2 text-sm">
              <FileText size={14} /> Devis Pennylane
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">N° de devis Pennylane</Label>
                <Input value={quoteForm.external_quote_number} onChange={(e) => setQuoteForm({ ...quoteForm, external_quote_number: e.target.value })} placeholder="DEV-310" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
              </div>
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><Paperclip size={11} /> Joindre PDF</Label>
                <Input type="file" accept=".pdf" onChange={(e) => setQuotePdfFile(e.target.files?.[0] || null)}
                  className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#FF3B30] file:text-white file:cursor-pointer"
                />
                {quotePdfFile && (
                  <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1"><Check size={11} /> {quotePdfFile.name}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Description de la prestation *</Label>
            <textarea
              required value={quoteForm.description} onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
              placeholder="Reprogrammation Stage 1 + FAP OFF..." rows={3}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white text-sm p-3 rounded-md resize-none focus:outline-none focus:border-white/20"
            />
          </div>

          {/* Amount */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
            <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2 text-sm">
              <Euro size={14} /> Montant (saisir HT ou TTC)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Montant HT (€)</Label>
                <Input type="number" step="0.01" value={quoteForm.amount_ht}
                  onChange={(e) => {
                    const ht = e.target.value;
                    setQuoteForm({ ...quoteForm, amount_ht: ht, amount_ttc: ht ? (parseFloat(ht) * 1.20).toFixed(2) : "" });
                  }}
                  placeholder="227.73" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">OU Montant TTC (€)</Label>
                <Input type="number" step="0.01" value={quoteForm.amount_ttc}
                  onChange={(e) => {
                    const ttc = e.target.value;
                    setQuoteForm({ ...quoteForm, amount_ttc: ttc, amount_ht: ttc ? (parseFloat(ttc) / 1.20).toFixed(2) : "" });
                  }}
                  placeholder="273.28" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm"
                />
              </div>
            </div>
            {(quoteForm.amount_ht || quoteForm.amount_ttc) && (
              <div className="mt-3 p-3 bg-[#141414] rounded-md border border-white/[0.06] grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider">HT</p>
                  <p className="text-white font-bold">{parseFloat(quoteForm.amount_ht || 0).toFixed(2)} €</p>
                </div>
                <div>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider">TTC</p>
                  <p className="text-white font-bold">{parseFloat(quoteForm.amount_ttc || 0).toFixed(2)} €</p>
                </div>
                <div>
                  <p className="text-amber-400 text-[10px] uppercase tracking-wider">Acompte 30%</p>
                  <p className="text-amber-400 font-bold">{(parseFloat(quoteForm.amount_ttc || 0) * 0.30).toFixed(2)} €</p>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Notes (visibles par le client)</Label>
            <textarea
              value={quoteForm.notes} onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
              placeholder="Notes pour le client..." rows={2}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white text-sm p-3 rounded-md resize-none focus:outline-none focus:border-white/20"
            />
          </div>

          <button
            type="submit" disabled={quoteLoading}
            className="bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/50 text-white px-6 py-2.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-2"
          >
            <Send size={15} />
            {quoteLoading ? "Création..." : "Créer le lien de paiement"}
          </button>
        </form>
      </SectionCard>

      {/* Filters */}
      <SectionCard title={null}>
        <div className="flex flex-wrap gap-3 items-center">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher client, description, N° devis..."
            icon={Search}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { key: "all", label: "Tous" },
            { key: "draft", label: "Brouillons" },
            { key: "sent", label: "Envoyés" },
            { key: "accepted", label: "Acceptés" },
            { key: "completed", label: "Terminés" },
            { key: "rejected", label: "Refusés" },
          ].map((s) => (
            <FilterPill key={s.key} active={statusFilter === s.key} onClick={() => setStatusFilter(s.key)}>
              {s.label}
            </FilterPill>
          ))}
        </div>
      </SectionCard>

      {/* Quotes table */}
      <SectionCard title={`Liste des devis (${list.length})`} icon={FileText}>
        {list.length === 0 ? (
          <EmptyState icon={FileText} title="Aucun devis" description="Créez un devis ci-dessus pour commencer." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left">
              <thead className="border-b border-white/[0.06]">
                <tr className="text-white/40 text-[11px] uppercase tracking-wider">
                  <th className="py-3 pr-4">Client</th>
                  <th className="py-3 pr-4">Description</th>
                  <th className="py-3 pr-4 text-right">TTC</th>
                  <th className="py-3 pr-4 text-right">Acompte</th>
                  <th className="py-3 pr-4">Statut</th>
                  <th className="py-3 pr-4">Paiement</th>
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-0 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((quote) => {
                  const sCfg = STATUS_CFG[quote.status] || STATUS_CFG.draft;
                  const pCfg = PAY_CFG[quote.payment_status] || PAY_CFG.pending;
                  return (
                    <tr key={quote.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 pr-4">
                        <p className="text-white font-medium text-sm truncate max-w-[180px]">{quote.customer_name}</p>
                        <p className="text-white/50 text-xs truncate max-w-[180px]">{quote.customer_email}</p>
                      </td>
                      <td className="py-3 pr-4 max-w-[240px]">
                        <p className="text-white text-sm truncate">{quote.description}</p>
                        {quote.vehicle_info && <p className="text-[#FF3B30] text-xs font-mono mt-0.5">{quote.vehicle_info}</p>}
                      </td>
                      <td className="py-3 pr-4 text-right text-emerald-400 font-bold">{quote.amount_ttc?.toFixed(2)} €</td>
                      <td className="py-3 pr-4 text-right text-amber-400">{quote.deposit_amount?.toFixed(2)} €</td>
                      <td className="py-3 pr-4"><StatusBadge config={sCfg} /></td>
                      <td className="py-3 pr-4"><StatusBadge config={pCfg} /></td>
                      <td className="py-3 pr-4 text-white/50 text-xs">
                        {quote.created_at ? new Date(quote.created_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                      <td className="py-3 pr-0">
                        <div className="flex gap-1.5 justify-end">
                          {quote.status === "draft" && (
                            <ActionButton icon={Send} label="Envoyer" color="blue" onClick={() => handleSendQuote(quote.id)} />
                          )}
                          {quote.status === "sent" && quote.payment_status === "pending" && (
                            <ActionButton icon={RotateCcw} label="Renvoyer" color="blue" onClick={() => handleSendQuote(quote.id)} />
                          )}
                          {(quote.payment_status === "deposit_paid" || quote.payment_status === "fully_paid") && !quote.appointment_id && (
                            <ActionButton icon={CalendarDays} label="RDV" color="green"
                              onClick={() => {
                                setShowCreateAppointmentFromQuote(quote);
                                setAppointmentFromQuoteForm({ date: "", time: "", service: quote.description || "Prestation", duration: 60 });
                              }}
                            />
                          )}
                          {quote.appointment_id && (
                            <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 px-2.5 py-1 rounded-md text-[11px]">
                              <Check size={11} /> RDV créé
                            </span>
                          )}
                          <ActionButton icon={Trash2} label="" color="red" onClick={() => handleDeleteQuote(quote.id)} title="Supprimer" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Create RDV modal */}
      {showCreateAppointmentFromQuote && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <CalendarDays size={18} className="text-[#FF3B30]" /> Créer un rendez-vous
              </h3>
              <button onClick={() => setShowCreateAppointmentFromQuote(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="bg-[#1C1C1E] border border-white/[0.06] rounded-lg p-3 mb-4 text-sm space-y-1.5">
              <div><span className="text-white/40 text-xs uppercase tracking-wider">Client</span><p className="text-white font-medium">{showCreateAppointmentFromQuote.customer_name}</p></div>
              <div><span className="text-white/40 text-xs uppercase tracking-wider">Prestation</span><p className="text-white">{showCreateAppointmentFromQuote.description}</p></div>
              <div><span className="text-white/40 text-xs uppercase tracking-wider">Véhicule</span><p className="text-[#FF3B30] font-mono">{showCreateAppointmentFromQuote.vehicle_info || "—"}</p></div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Date</Label>
                <Input type="date" value={appointmentFromQuoteForm.date}
                  onChange={(e) => setAppointmentFromQuoteForm({ ...appointmentFromQuoteForm, date: e.target.value })}
                  min={new Date().toISOString().split("T")[0]}
                  className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Heure</Label>
                <Select value={appointmentFromQuoteForm.time} onValueChange={(v) => setAppointmentFromQuoteForm({ ...appointmentFromQuoteForm, time: v })}>
                  <SelectTrigger className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-white/10">
                    {timeSlots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Service</Label>
                <Input value={appointmentFromQuoteForm.service} onChange={(e) => setAppointmentFromQuoteForm({ ...appointmentFromQuoteForm, service: e.target.value })} className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
              </div>
              <div>
                <Label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Durée</Label>
                <Select value={appointmentFromQuoteForm.duration.toString()} onValueChange={(v) => setAppointmentFromQuoteForm({ ...appointmentFromQuoteForm, duration: parseInt(v) })}>
                  <SelectTrigger className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-white/10">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 heure</SelectItem>
                    <SelectItem value="90">1h30</SelectItem>
                    <SelectItem value="120">2 heures</SelectItem>
                    <SelectItem value="180">3 heures</SelectItem>
                    <SelectItem value="240">4 heures</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setShowCreateAppointmentFromQuote(null)}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 rounded-md text-sm transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateAppointmentFromQuote}
                className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] text-white py-2 rounded-md text-sm font-bold transition-colors flex items-center justify-center gap-2"
              >
                <Check size={15} /> Créer le RDV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotesTab;
